import React from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import {FormikContext} from 'formik';
import {useWizard} from './WizardNode';

function normalizeSx(sx: any) {
 if (!sx || typeof sx !== 'object' || Array.isArray(sx)) return sx;
 const next = {...sx};

 if (next.bgColor !== undefined && next.bgcolor === undefined) {
  next.bgcolor = next.bgColor;
  delete next.bgColor;
 }

 return next;
}

function getPlacementSx(placement: string, fixed: boolean) {
 if (!placement) return {};

 const isVertical = placement === 'left' || placement === 'right';
 const borderKey =
  placement === 'bottom' ? 'borderTop' :
  placement === 'left' ? 'borderRight' :
  placement === 'right' ? 'borderLeft' :
  'borderBottom';

 return {
  ...(fixed
   ? {
    position: 'fixed',
    zIndex: 10,
    ...(placement === 'bottom' ? {left: 0, right: 0, bottom: 0} : {}),
    ...(placement === 'top' ? {left: 0, right: 0, top: 0} : {}),
    ...(placement === 'left' ? {left: 0, top: 0, bottom: 0} : {}),
    ...(placement === 'right' ? {right: 0, top: 0, bottom: 0} : {}),
   }
   : {}),
  [borderKey]: '1px solid',
  borderColor: 'divider',
  mb: placement === 'top' ? 2 : 0,
  mt: placement === 'bottom' ? 2 : 0,
  width: isVertical ? 'auto' : '100%',
 };
}

function renderIcon(icon: any, tab: any, selected: boolean, iconSx: any) {
 if (!icon) return undefined;

 const iconProps = {
  active: selected,
  selected,
  index: tab?.index,
  item: tab,
 };

 if (React.isValidElement(icon)) {
  return React.cloneElement(icon as any, iconProps);
 }

 if (typeof icon === 'function') {
  return React.createElement(icon, iconProps);
 }

 return (
  <Box component="span" sx={{fontSize: 'inherit', lineHeight: 1, ...normalizeSx(iconSx)}}>
   {icon}
  </Box>
 );
}

export default function WizardTabsNode({
 sx,
 tabsSx,
 tabSx,
 activeTabSx,
 labelSx,
 iconSx,
 hideDisabledScrollButtons = true,
 placement = 'top',
 fixed = false,
 iconPosition = 'top',
 showLabel = true,
 orientation,
 ...props
}: any) {
 const wizard = useWizard();
 const formik: any = React.useContext(FormikContext as any);
 const resolvedOrientation =
  orientation || (placement === 'left' || placement === 'right' ? 'vertical' : 'horizontal');

 const touchCurrentFields = () => {
  const touched = (wizard?.fields || []).reduce(
   (acc: any, field: string) => ({...acc, [field]: true}),
   {}
  );

  if (Object.keys(touched).length) {
   formik?.setTouched?.({...formik?.touched, ...touched}, true);
  }
 };

 const handleChange = async (_event: any, nextStep: number) => {
  if (nextStep === wizard?.step) return;

  const errors = await formik?.validateForm?.();
  touchCurrentFields();
  wizard?.reportStepErrors?.(wizard.step, errors);
  wizard?.goTo?.(nextStep);
 };

 return (
  <Box
   sx={{
    ...getPlacementSx(placement, Boolean(fixed)),
    ...(hideDisabledScrollButtons
     ? {
      '& .MuiTabs-scrollButtons.Mui-disabled': {
       width: 0,
       minWidth: 0,
       opacity: 0,
       overflow: 'hidden',
      },
     }
     : {}),
    ...normalizeSx(sx),
   }}
  >
   <Tabs
    value={wizard?.step || 0}
    onChange={handleChange}
    orientation={resolvedOrientation}
    sx={tabsSx}
    {...props}
   >
    {(wizard?.tabs || []).map((tab: any, index: number) => {
     const count = wizard?.stepErrors?.[index] || 0;
     const selected = (wizard?.step || 0) === index;
     const icon = renderIcon(tab?.icon, tab, selected, iconSx);

     return (
      <Tab
       key={index}
       disabled={tab?.disabled}
       icon={icon}
       iconPosition={tab?.iconPosition || iconPosition}
       label={
        <Badge color="error" badgeContent={count} invisible={!count}>
         <Box component="span" sx={{display: showLabel ? 'inline' : 'none', ...normalizeSx(labelSx)}}>
          {tab?.label}
         </Box>
        </Badge>
       }
       sx={{
        ...normalizeSx(tabSx),
        ...normalizeSx(tab?.sx),
        ...(selected ? normalizeSx(activeTabSx) : {}),
       }}
      />
     );
    })}
   </Tabs>
  </Box>
 );
}
